import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Activity,
  AlertOctagon,
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  ExternalLink,
  Flame,
  Globe,
  Layers,
  Search,
  ShieldCheck,
  TrendingUp,
  XCircle,
  HelpCircle,
} from "lucide-react";
import type {
  AdsTrackingClientPayload,
  AdsCampaignRow,
  LandingPageTrackingRow,
  AdsTrackingAlert,
} from "../../types";

interface Props {
  data: AdsTrackingClientPayload;
  headerSlotLeft?: HTMLDivElement | null;
  headerSlotActions?: HTMLDivElement | null;
}

type TabKey = "resumen" | "campanas" | "landings" | "alertas";

export function AdsTrackingDesktop({ data, headerSlotLeft, headerSlotActions }: Props) {
  const [activeTab, setActiveTab] = useState<TabKey>("resumen");
  const [campaignSearch, setCampaignSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState<string>("all");
  const [landingSearch, setLandingSearch] = useState("");
  const [selectedAlert, setSelectedAlert] = useState<AdsTrackingAlert | null>(null);

  const { summary, campaigns, landing_pages, alerts, daily_history } = data;

  // Filtrado de Campañas
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((c) => {
      const matchPlat = platformFilter === "all" || c.platform === platformFilter;
      const matchQuery =
        !campaignSearch ||
        c.campaign_name.toLowerCase().includes(campaignSearch.toLowerCase());
      return matchPlat && matchQuery;
    });
  }, [campaigns, platformFilter, campaignSearch]);

  // Filtrado de Landings
  const filteredLandings = useMemo(() => {
    return landing_pages.filter((l) => {
      return (
        !landingSearch ||
        l.url.toLowerCase().includes(landingSearch.toLowerCase()) ||
        (l.ga4_id && l.ga4_id.toLowerCase().includes(landingSearch.toLowerCase())) ||
        (l.gtm_container_id && l.gtm_container_id.toLowerCase().includes(landingSearch.toLowerCase()))
      );
    });
  }, [landing_pages, landingSearch]);

  const navPills = (
    <div className="at-nav-pills">
      <button
        type="button"
        className={`at-nav-pill ${activeTab === "resumen" ? "is-active" : ""}`}
        onClick={() => setActiveTab("resumen")}
      >
        <TrendingUp size={14} /> Resumen & Pacing
      </button>
      <button
        type="button"
        className={`at-nav-pill ${activeTab === "campanas" ? "is-active" : ""}`}
        onClick={() => setActiveTab("campanas")}
      >
        <Layers size={14} /> Campañas Ads ({campaigns.length})
      </button>
      <button
        type="button"
        className={`at-nav-pill ${activeTab === "landings" ? "is-active" : ""}`}
        onClick={() => setActiveTab("landings")}
      >
        <Globe size={14} /> Landings & Tracking ({landing_pages.length})
      </button>
      <button
        type="button"
        className={`at-nav-pill ${activeTab === "alertas" ? "is-active" : ""}`}
        onClick={() => setActiveTab("alertas")}
      >
        <AlertTriangle size={14} /> Alertas ({alerts.length})
        {summary.p0_alerts > 0 && <span className="at-pill-badge">{summary.p0_alerts} P0</span>}
      </button>
    </div>
  );

  const actionButtons = (
    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
      <button
        type="button"
        className="at-filter-btn is-active"
        onClick={() => window.location.reload()}
        style={{ display: "flex", alignItems: "center", gap: "6px" }}
      >
        <Clock size={13} /> Refrescar
      </button>
    </div>
  );

  return (
    <div className="at-desktop-container">
      {headerSlotLeft && createPortal(navPills, headerSlotLeft)}
      {headerSlotActions && createPortal(actionButtons, headerSlotActions)}

      {/* Top Banner KPI Grid */}
      <div className="at-kpi-grid">
        <div className={`at-kpi-card ${summary.health_score === null ? "" : summary.health_score < 70 ? "is-alert" : summary.health_score < 85 ? "is-warning" : "is-success"}`}>
          <div className="at-kpi-label">
            <span>Score GTM / Tracking</span>
            <ShieldCheck size={16} />
          </div>
          <div className="at-kpi-val">
            {summary.health_score !== null ? `${summary.health_score} / 100` : "Sin auditar"}
          </div>
          <div className="at-kpi-sub">
            {summary.health_score !== null
              ? summary.health_score >= 80 ? "Auditoría BigQuery OK" : "Atención requerida"
              : "Sin registro en vw_dm_gtm_health"}
          </div>
        </div>

        <div className="at-kpi-card">
          <div className="at-kpi-label">
            <span>Pacing Intradía (Hoy)</span>
            <Flame size={16} />
          </div>
          <div className="at-kpi-val">
            {summary.pacing_intraday_pct !== null ? `${summary.pacing_intraday_pct}%` : "No medido"}
          </div>
          <div className="at-kpi-sub">
            {summary.spend_today !== null
              ? `$${Math.round(summary.spend_today).toLocaleString("es-CL")} gastados hoy`
              : "Requiere conector oficial Ads MCP"}
          </div>
        </div>

        <div className="at-kpi-card">
          <div className="at-kpi-label">
            <span>Pacing Mes Acumulado (MTD)</span>
            <Activity size={16} />
          </div>
          <div className="at-kpi-val">
            {summary.pacing_mtd_pct !== null ? `${summary.pacing_mtd_pct}%` : "Sin presupuesto"}
          </div>
          <div className="at-kpi-sub">
            {summary.spend_mtd !== null && summary.budget_mtd !== null
              ? `$${Math.round(summary.spend_mtd).toLocaleString("es-CL")} / $${Math.round(summary.budget_mtd).toLocaleString("es-CL")}`
              : "Fuente: vw_dm_budget_status_mtd"}
          </div>
        </div>

        <div className={`at-kpi-card ${summary.p0_alerts > 0 ? "is-alert" : summary.p1_alerts > 0 ? "is-warning" : ""}`}>
          <div className="at-kpi-label">
            <span>Alertas Reales</span>
            <AlertOctagon size={16} />
          </div>
          <div className="at-kpi-val">{summary.p0_alerts + summary.p1_alerts + summary.p2_alerts}</div>
          <div className="at-kpi-sub">
            <span style={{ color: "var(--at-p0)", fontWeight: 700 }}>{summary.p0_alerts} P0</span> ·{" "}
            <span style={{ color: "var(--at-p1)", fontWeight: 600 }}>{summary.p1_alerts} P1</span> (en vw_dm_gtm_findings)
          </div>
        </div>
      </div>

      {/* SECCIÓN 1: RESUMEN Y PACING */}
      {activeTab === "resumen" && (
        <>
          <div className="at-pacing-panel">
            <div className="at-panel-header">
              <div className="at-panel-title">
                <TrendingUp size={18} /> Pacing Mensual Acumulado MTD (BigQuery)
              </div>
              <span style={{ fontSize: "12px", color: "var(--at-muted)" }}>
                Fuente: <code>mkt_bi.vw_dm_budget_status_mtd</code>
              </span>
            </div>

            <div className="at-pacing-compare-grid">
              <div className="at-pacing-box">
                <div className="at-pacing-box-title">
                  <span>Gasto MTD vs Presupuesto del Mes</span>
                  <strong>{summary.pacing_mtd_pct !== null ? `${summary.pacing_mtd_pct}%` : "No disponible"}</strong>
                </div>
                {summary.pacing_mtd_pct !== null ? (
                  <div className="at-progress-track">
                    <div
                      className={`at-progress-bar ${summary.pacing_mtd_pct > 110 ? "is-over" : summary.pacing_mtd_pct < 85 ? "is-normal" : "is-good"}`}
                      style={{ width: `${Math.min(100, summary.pacing_mtd_pct)}%` }}
                    />
                  </div>
                ) : (
                  <p style={{ fontSize: "12px", color: "var(--at-muted)", margin: "8px 0" }}>
                    Este cliente no tiene filas de presupuesto mensual en <code>vw_dm_budget_status_mtd</code>.
                  </p>
                )}
              </div>

              <div className="at-pacing-box">
                <div className="at-pacing-box-title">
                  <span>Medición Intradía</span>
                  <span className="at-badge neutral">Sin conector</span>
                </div>
                <p style={{ fontSize: "12px", color: "var(--at-muted)", margin: "8px 0" }}>
                  Para monitorear el gasto de hoy en curso sin saturar BigQuery se debe activar el conector oficial de solo lectura de Google Ads MCP y Meta Ads MCP.
                </p>
              </div>
            </div>
          </div>

          {/* Histórico Real 14 Días */}
          <div className="at-table-container">
            <div className="at-table-toolbar">
              <div style={{ fontWeight: 700, fontSize: "14px" }}>
                Historial de Gasto y Presupuesto Diario
              </div>
              <span style={{ fontSize: "12px", color: "var(--at-muted)" }}>
                Fuente: <code>mkt_bi.vw_dm_spend_budget_daily</code>
              </span>
            </div>
            {daily_history.length > 0 ? (
              <table className="at-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Gasto Diario Real</th>
                    <th>Presupuesto Diario</th>
                    <th>Diferencia (Gap)</th>
                    <th>Cumplimiento (%)</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {daily_history.map((row) => (
                    <tr key={row.date}>
                      <td style={{ fontWeight: 600 }}>{row.date}</td>
                      <td>${Math.round(row.spend_daily).toLocaleString("es-CL")}</td>
                      <td>${Math.round(row.budget_daily).toLocaleString("es-CL")}</td>
                      <td style={{ color: row.gap_daily < 0 ? "var(--at-p0)" : "var(--at-success)" }}>
                        {row.gap_daily < 0 ? "-" : "+"}${Math.abs(Math.round(row.gap_daily)).toLocaleString("es-CL")}
                      </td>
                      <td>
                        {row.pacing_pct !== null ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div style={{ width: "80px", height: "6px", background: "var(--at-border)", borderRadius: "99px", overflow: "hidden" }}>
                              <div
                                style={{
                                  width: `${Math.min(100, row.pacing_pct)}%`,
                                  height: "100%",
                                  background: row.pacing_pct > 115 ? "var(--at-p0)" : row.pacing_pct < 80 ? "var(--at-p2)" : "var(--at-success)",
                                }}
                              />
                            </div>
                            <span style={{ fontWeight: 600 }}>{row.pacing_pct}%</span>
                          </div>
                        ) : (
                          <span style={{ color: "var(--at-muted)" }}>N/A</span>
                        )}
                      </td>
                      <td>
                        {row.pacing_pct !== null && row.pacing_pct > 115 ? (
                          <span className="at-badge p0">Sobregasto</span>
                        ) : row.pacing_pct !== null && row.pacing_pct < 80 ? (
                          <span className="at-badge p2">Subgasto</span>
                        ) : row.pacing_pct !== null ? (
                          <span className="at-badge success">Óptimo</span>
                        ) : (
                          <span className="at-badge neutral">Sin meta</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: "32px", textAlign: "center", color: "var(--at-muted)", fontSize: "13px" }}>
                No se registraron registros de gasto en <code>mkt_bi.vw_dm_spend_budget_daily</code> para este cliente en los últimos 14 días.
              </div>
            )}
          </div>
        </>
      )}

      {/* SECCIÓN 2: CAMPAÑAS ADS */}
      {activeTab === "campanas" && (
        <div className="at-table-container">
          <div className="at-table-toolbar">
            <div style={{ fontWeight: 700, fontSize: "14px" }}>
              Campañas con gasto en los últimos 7 días
            </div>
            <span style={{ fontSize: "12px", color: "var(--at-muted)" }}>
              {summary.active_campaigns} activas de {campaigns.length}
            </span>
          </div>
          {campaigns.length > 0 ? (
            <table className="at-table">
              <thead>
                <tr>
                  <th>Plataforma</th>
                  <th>Nombre de Campaña</th>
                  <th>Estado</th>
                  <th>Gasto 7 días</th>
                  <th>Último día con gasto</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.map((c) => (
                  <tr key={c.campaign_id}>
                    <td><span className={`at-plat-tag ${c.platform}`}>{c.platform}</span></td>
                    <td><strong>{c.campaign_name}</strong></td>
                    <td><span className={`at-badge ${c.is_active ? "success" : ""}`}>{c.status ?? "N/D"}</span></td>
                    <td>${Math.round(c.spend_7d).toLocaleString("es-CL")}</td>
                    <td>{c.last_spend_date ?? "N/D"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: "48px 24px", textAlign: "center" }}>
              <HelpCircle size={32} style={{ color: "var(--at-muted)", margin: "0 auto 12px" }} />
              <h4 style={{ margin: "0 0 6px", fontSize: "15px" }}>Sin campañas con gasto en los últimos 7 días</h4>
              <p style={{ color: "var(--at-muted)", fontSize: "13px", maxWidth: "480px", margin: "0 auto" }}>
                El gasto de hoy requiere los conectores de Google Ads y Meta en modo lectura; BigQuery se actualiza a diario.
              </p>
            </div>
          )}
        </div>
      )}

      {/* SECCIÓN 3: LANDINGS Y ATRIBUCIÓN REAL */}
      {activeTab === "landings" && (
        <div className="at-table-container">
          <div className="at-table-toolbar">
            <div style={{ fontWeight: 700, fontSize: "14px" }}>
              Landings & Atribución (Datos Reales de Escaneo Web)
            </div>
            <span style={{ fontSize: "12px", color: "var(--at-muted)" }}>
              Fuente: <code>wolf_compliance.site_scan_snapshot</code>
            </span>
          </div>

          {landing_pages.length > 0 ? (
            <table className="at-table">
              <thead>
                <tr>
                  <th>Landing URL</th>
                  <th>HTTP Status</th>
                  <th>Contenedor GTM</th>
                  <th>GA4 Stream ID</th>
                  <th>Meta Pixel ID</th>
                  <th>Google Ads Linker</th>
                  <th>Banner CMP</th>
                  <th>Pre-interacción</th>
                  <th>Cookies Observadas</th>
                  <th>Hallazgos en Escaneo</th>
                </tr>
              </thead>
              <tbody>
                {filteredLandings.map((l) => (
                  <tr key={l.url}>
                    <td>
                      <div style={{ fontWeight: 600, color: "var(--at-ink)" }}>{l.url}</div>
                    </td>
                    <td>
                      {l.http_status !== null ? (
                        <span className="at-badge success">{l.http_status}</span>
                      ) : (
                        <span className="at-badge neutral">No medido</span>
                      )}
                    </td>
                    <td>
                      {l.trackers_in_container > 0 ? (
                        <span className="at-badge success">{l.trackers_in_container} tags dentro</span>
                      ) : l.trackers_outside_container > 0 ? (
                        <span className="at-badge neutral">0 dentro ({l.trackers_outside_container} fuera)</span>
                      ) : (
                        <span className="at-badge neutral">No detectado</span>
                      )}
                    </td>
                    <td>
                      {l.ga4_id ? (
                        <span className="at-badge success">{l.ga4_id}</span>
                      ) : (
                        <span className="at-badge neutral">No detectado</span>
                      )}
                    </td>
                    <td>
                      {l.meta_pixel_id ? (
                        <span className="at-badge success">{l.meta_pixel_id}</span>
                      ) : (
                        <span className="at-badge neutral">No detectado</span>
                      )}
                    </td>
                    <td>
                      {l.ads_conversion_linker === "FOUND" ? (
                        <span className="at-badge success">Activo</span>
                      ) : (
                        <span className="at-badge neutral">No detectado</span>
                      )}
                    </td>
                    <td>
                      {l.consent_banner_found ? (
                        <span className="at-badge success">Detectado</span>
                      ) : (
                        <span className="at-badge neutral">No detectado</span>
                      )}
                    </td>
                    <td>
                      {l.fired_before_interaction ? (
                        <span className="at-badge p1">Sí</span>
                      ) : (
                        <span className="at-badge success">No</span>
                      )}
                    </td>
                    <td style={{ maxWidth: "160px", fontSize: "11px", wordBreak: "break-all" }}>
                      {l.cookies_set && l.cookies_set.length > 0 ? l.cookies_set.join(", ") : <span style={{ color: "var(--at-muted)" }}>Ninguna</span>}
                    </td>
                    <td>
                      {l.findings.length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: "16px", fontSize: "11px", color: "var(--at-muted)" }}>
                          {l.findings.map((f, i) => (
                            <li key={i}>{f}</li>
                          ))}
                        </ul>
                      ) : (
                        <span style={{ color: "var(--at-muted)", fontSize: "12px" }}>Sin anomalías en escaneo</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: "48px 24px", textAlign: "center" }}>
              <Globe size={32} style={{ color: "var(--at-muted)", margin: "0 auto 12px" }} />
              <h4 style={{ margin: "0 0 6px", fontSize: "15px" }}>Sin Escaneo Web Registrado</h4>
              <p style={{ color: "var(--at-muted)", fontSize: "13px", maxWidth: "480px", margin: "0 auto" }}>
                No hay registros de escaneo para este cliente en <code>wolf_compliance.site_scan_snapshot</code>.
                Ejecuta el escáner Playwright: <code>python sys_jobs/pipelines_ingesta/python/site_scan/scan_site_tracking.py --client-slug {data.client_slug} --write</code>.
              </p>
            </div>
          )}
        </div>
      )}

      {/* SECCIÓN 4: ALERTAS REALES */}
      {activeTab === "alertas" && (
        <div style={{ display: "grid", gridTemplateColumns: selectedAlert ? "1fr 1fr" : "1fr", gap: "20px" }}>
          <div className="at-table-container">
            <div className="at-table-toolbar">
              <div style={{ fontWeight: 700, fontSize: "14px" }}>
                Hallazgos Reales de Auditoría GTM
              </div>
              <span style={{ fontSize: "12px", color: "var(--at-muted)" }}>
                Fuente: <code>wolf_compliance.vw_dm_gtm_findings</code>
              </span>
            </div>

            {alerts.length > 0 ? (
              <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                {alerts.map((al) => (
                  <div
                    key={al.id}
                    onClick={() => setSelectedAlert(al)}
                    style={{
                      background: selectedAlert?.id === al.id ? "var(--at-accent-tint)" : "var(--at-surface-container)",
                      border: `1px solid ${selectedAlert?.id === al.id ? "var(--at-accent)" : "var(--at-border)"}`,
                      borderRadius: "8px",
                      padding: "16px",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span className={`at-badge ${al.severity.toLowerCase()}`}>{al.severity}</span>
                        <span className="at-badge neutral">{al.category}</span>
                      </div>
                      <ArrowUpRight size={16} style={{ color: "var(--at-muted)" }} />
                    </div>
                    <h4 style={{ margin: "0 0 6px", fontSize: "14px", color: "var(--at-ink)" }}>{al.title}</h4>
                    <p style={{ margin: 0, fontSize: "12px", color: "var(--at-muted)", lineHeight: 1.4 }}>
                      {al.description}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: "48px 24px", textAlign: "center" }}>
                <CheckCircle2 size={32} style={{ color: "var(--at-success)", margin: "0 auto 12px" }} />
                <h4 style={{ margin: "0 0 6px", fontSize: "15px" }}>0 Hallazgos en BigQuery</h4>
                <p style={{ color: "var(--at-muted)", fontSize: "13px" }}>
                  No existen alertas activas registradas en <code>vw_dm_gtm_findings</code> para este cliente.
                </p>
              </div>
            )}
          </div>

          {selectedAlert && (
            <div className="at-pacing-panel" style={{ position: "sticky", top: "20px" }}>
              <div className="at-panel-header">
                <div className="at-panel-title">
                  <AlertOctagon size={18} style={{ color: "var(--at-p0)" }} /> Detalle del Hallazgo
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedAlert(null)}
                  style={{ background: "none", border: "none", color: "var(--at-muted)", cursor: "pointer" }}
                >
                  Cerrar
                </button>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <span className={`at-badge ${selectedAlert.severity.toLowerCase()}`} style={{ marginRight: "8px" }}>
                  Severidad {selectedAlert.severity}
                </span>
                <span className="at-badge neutral">{selectedAlert.category}</span>
              </div>

              <h3 style={{ fontSize: "16px", color: "var(--at-ink)", margin: "0 0 10px" }}>
                {selectedAlert.title}
              </h3>
              <p style={{ fontSize: "13px", color: "var(--at-muted)", lineHeight: 1.5, marginBottom: "20px" }}>
                {selectedAlert.description}
              </p>

              <div style={{ background: "var(--at-surface-container)", padding: "16px", borderRadius: "8px", border: "1px solid var(--at-border)" }}>
                <h5 style={{ margin: "0 0 8px", fontSize: "12px", textTransform: "uppercase", color: "var(--at-accent)", letterSpacing: "0.05em" }}>
                  Acción
                </h5>
                <p style={{ margin: 0, fontSize: "13px", color: "var(--at-ink)", lineHeight: 1.5 }}>
                  {selectedAlert.action_recommended}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
